import * as React from 'react';
import { forwardRef, useContext } from 'react';
import { useFeatures } from './features/use-features.js';
import { MotionConfigContext } from '../context/MotionConfigContext.js';
import { MotionContext } from '../context/MotionContext/index.js';
import { useVisualElement } from './utils/use-visual-element.js';
import { useMotionRef } from './utils/use-motion-ref.js';
import { useCreateMotionContext } from '../context/MotionContext/create.js';
import { loadFeatures } from './features/definitions.js';
import { isBrowser } from '../utils/is-browser.js';
/**
 * Create a `motion` component.
 *
 * This function accepts a Component argument, which can be either a string (ie "div"
 * for `motion.div`), or an actual React component.
 *
 * Alongside this is a config option which provides a way of rendering the provided
 * component "offline", or outside the React render cycle.
 *
 * @internal
 */

function createMotionComponent(_a) {
  var preloadedFeatures = _a.preloadedFeatures,
      createVisualElement = _a.createVisualElement,
      useRender = _a.useRender,
      useVisualState = _a.useVisualState,
      Component = _a.Component;
  preloadedFeatures && loadFeatures(preloadedFeatures);

  function MotionComponent(props, externalRef) {
    /**
     * If we're rendering in a static environment, we only visually update the component
     * as a result of a React-rerender rather than interactions or animations. This
     * means we don't need to load additional memory structures like VisualElement,
     * or any gesture/animation features.
     */
    var isStatic = useContext(MotionConfigContext).isStatic;
    var features = null;
    /**
     * Create the tree context. This is memoized and will only trigger renders
     * when the current tree variant changes in static mode.
     */

    var context = useCreateMotionContext(props, isStatic);
    /**
     *
     */

    var visualState = useVisualState(props, isStatic);

    if (!isStatic && isBrowser) {
      /**
       * Create a VisualElement for this component. A VisualElement provides a common
       * interface to renderer-specific APIs (ie DOM/Three.js etc) as well as
       * providing a way of rendering to these APIs outside of the React render loop
       * for more performant animations and interactions
       */
      context.visualElement = useVisualElement(Component, visualState, props, createVisualElement);
      /**
       * Load Motion gesture and animation features. These are rendered as renderless
       * components so each feature can optionally make use of React lifecycle methods.
       *
       * TODO: The intention is to move these away from a React-centric to a
       * VisualElement-centric lifecycle scheme.
       */

      features = useFeatures(props, context.visualElement, preloadedFeatures);
    }
    /**
     * The mount order and hierarchy is specific to ensure our element ref
     * is hydrated by the time features fire their effects.
     */


    return React.createElement(React.Fragment, null, React.createElement(MotionContext.Provider, {
      value: context
    }, useRender(Component, props, useMotionRef(visualState, context.visualElement, externalRef), visualState, isStatic)), features);
  }

  return forwardRef(MotionComponent);
}

export { createMotionComponent };